import { useState } from "react";
import { Pressable, ActivityIndicator, Switch } from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useRouter } from "expo-router";
import { addDoc, collection, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useMembers } from "@/hooks/useMembers";
import { createPost } from "@/lib/services";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import { DateInput, TimeInput } from "@/components/DateTimeInputs";

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch { return dateStr; }
}

const Section = styled.View`padding: 0 20px; margin-top: 16px;`;
const Label = styled.Text`font-size: 11px; font-weight: ${font.bold}; color: ${colors.inkMuted}; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px;`;
const Input = styled.TextInput`background-color: ${colors.bg}; border: 1px solid ${colors.line}; border-radius: ${radius.md}px; padding: 12px 14px; font-size: 15px; color: ${colors.ink};`;
const TextArea = styled.TextInput`background-color: ${colors.bg}; border: 1px solid ${colors.line}; border-radius: ${radius.md}px; padding: 12px 14px; font-size: 15px; color: ${colors.ink}; min-height: 80px;`;

const PillRow = styled.View`flex-direction: row; gap: 8px; flex-wrap: wrap;`;
const Pill = styled.View<{ active: boolean }>`
  padding: 8px 14px; border-radius: 999px;
  background-color: ${(p) => p.active ? colors.brand : colors.bg};
  border: 1px solid ${(p) => p.active ? colors.brand : colors.line};
`;
const PillText = styled.Text<{ active: boolean }>`font-size: 13px; font-weight: ${font.bold}; color: ${(p) => p.active ? "#fff" : colors.inkSecondary};`;

const ToggleRow = styled.View`flex-direction: row; align-items: center; justify-content: space-between; padding: 12px 0;`;
const ToggleLabel = styled.Text`font-size: 15px; font-weight: ${font.bold}; color: ${colors.ink};`;
const ToggleSub = styled.Text`font-size: 12px; color: ${colors.inkMuted}; font-weight: ${font.medium};`;

const MemberRow = styled.View<{ selected: boolean }>`
  flex-direction: row; align-items: center; padding: 10px 0;
  opacity: ${(p) => p.selected ? 1 : 0.6};
`;
const MemberAvatar = styled.View`width: 32px; height: 32px; border-radius: 16px; background-color: ${colors.brandLight}; align-items: center; justify-content: center; margin-right: 10px;`;
const MemberAvatarText = styled.Text`font-size: 12px; font-weight: ${font.extrabold}; color: ${colors.brandDark};`;
const MemberName = styled.Text`font-size: 14px; font-weight: ${font.bold}; color: ${colors.ink}; flex: 1;`;
const Checkbox = styled.View<{ checked: boolean }>`
  width: 22px; height: 22px; border-radius: 11px;
  border: 2px solid ${(p) => p.checked ? colors.brand : colors.line};
  background-color: ${(p) => p.checked ? colors.brand : colors.white};
  align-items: center; justify-content: center;
`;
const CheckMark = styled.Text`color: #fff; font-size: 12px; font-weight: ${font.bold};`;

const CreateBtn = styled.View`background-color: ${colors.brand}; border-radius: ${radius.card}px; padding: 16px; align-items: center; margin: 24px 20px;`;
const CreateBtnText = styled.Text`color: #fff; font-size: 16px; font-weight: ${font.bold};`;

const Spacer = styled.View`height: 32px;`;

export default function CreateEventScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { members } = useMembers(club?.id);
  const { alertProps, alert } = useAlert();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("19:00");
  const [location, setLocation] = useState("");
  const [type, setType] = useState<"open" | "invite-only">("open");
  const [isPaid, setIsPaid] = useState(false);
  const [fee, setFee] = useState("");
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const toggleInvite = (userId: string) => {
    setInvitedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!club || !user || !membership || !title.trim() || !date) return;
    setSaving(true);
    try {
      const feeInPence = isPaid ? Math.round(parseFloat(fee || "0") * 100) : 0;

      await addDoc(collection(db, "clubs", club.id, "events"), {
        title: title.trim(),
        description: description.trim(),
        date,
        time,
        location: location.trim(),
        type,
        isPaid,
        fee: feeInPence,
        invitedMembers: type === "invite-only" ? Array.from(invitedIds) : [],
        createdBy: user.uid,
        createdByName: membership.displayName,
        clubId: club.id,
        clubName: club.name,
        createdAt: Timestamp.now(),
        goingCount: 0,
        notGoingCount: 0,
        maybeCount: 0,
      });

      // Auto-create feed post
      const feeText = isPaid && feeInPence > 0 ? ` · £${(feeInPence / 100).toFixed(0)} per person` : "";
      const typeText = type === "invite-only" ? " (Invite only)" : "";
      await createPost(club.id, {
        authorId: user.uid,
        authorName: membership.displayName,
        authorRole: membership.role,
        text: `📅 ${title.trim()}${typeText}\n${formatDate(date)} · ${time} · ${location.trim()}${feeText}`,
        visibility: "internal",
        clubName: club.name,
      });

      await alert({ title: "Event created!", emoji: "📅" });
      router.back();
    } catch (err: any) {
      await alert({ title: "Error", emoji: "😕", message: err.message, buttonColor: colors.danger });
    } finally {
      setSaving(false);
    }
  };

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
      <ContentWidth>
        <Section>
          <Label>Title</Label>
          <Input placeholder="e.g. End of Season Dinner" placeholderTextColor={colors.inkMuted} value={title} onChangeText={setTitle} />
        </Section>

        <Section>
          <Label>Description</Label>
          <TextArea placeholder="What's the event about?" placeholderTextColor={colors.inkMuted} value={description} onChangeText={setDescription} multiline />
        </Section>

        <Section>
          <Label>Date</Label>
          <DateInput value={date} onChange={setDate} />
        </Section>

        <Section>
          <Label>Time</Label>
          <TimeInput value={time} onChange={setTime} />
        </Section>

        <Section>
          <Label>Location</Label>
          <Input placeholder="e.g. The Crown Hotel, Peterborough" placeholderTextColor={colors.inkMuted} value={location} onChangeText={setLocation} />
        </Section>

        <Section>
          <Label>Type</Label>
          <PillRow>
            <Pressable onPress={() => setType("open")}><Pill active={type === "open"}><PillText active={type === "open"}>Open to all</PillText></Pill></Pressable>
            <Pressable onPress={() => setType("invite-only")}><Pill active={type === "invite-only"}><PillText active={type === "invite-only"}>Invite only</PillText></Pill></Pressable>
          </PillRow>
        </Section>

        {type === "invite-only" && (
          <Section>
            <Label>Invite members ({invitedIds.size} selected)</Label>
            {members.map((m) => (
              <Pressable key={m.id} onPress={() => toggleInvite(m.userId)}>
                <MemberRow selected={invitedIds.has(m.userId)}>
                  <MemberAvatar><MemberAvatarText>{m.displayName?.[0]?.toUpperCase()}</MemberAvatarText></MemberAvatar>
                  <MemberName>{m.displayName}</MemberName>
                  <Checkbox checked={invitedIds.has(m.userId)}>
                    {invitedIds.has(m.userId) && <CheckMark>✓</CheckMark>}
                  </Checkbox>
                </MemberRow>
              </Pressable>
            ))}
          </Section>
        )}

        <Section>
          <ToggleRow>
            <ToggleLabel>Paid event</ToggleLabel>
            <Switch value={isPaid} onValueChange={setIsPaid} trackColor={{ true: colors.brand, false: colors.line }} />
          </ToggleRow>
          {isPaid && (
            <>
              <Label>Fee (£)</Label>
              <Input placeholder="e.g. 30" placeholderTextColor={colors.inkMuted} value={fee} onChangeText={setFee} keyboardType="numeric" />
              <ToggleSub style={{ marginTop: 4 }}>Members will see this in their fees when they RSVP</ToggleSub>
            </>
          )}
        </Section>

        <Pressable onPress={handleCreate} disabled={saving || !title.trim() || !date} style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}>
          <CreateBtn>
            {saving ? <ActivityIndicator color="#fff" /> : <CreateBtnText>Create Event</CreateBtnText>}
          </CreateBtn>
        </Pressable>

        <Spacer />
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </KeyboardAwareScroll>
  );
}
